import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { getArticleUrl } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import { SITE_URL, SITE_NAME, SITE_LOGO_URL } from '@/lib/site';

interface Props {
  article: Article;
  relatedArticles: Article[];
}

export default function ArticleLayout({ article, relatedArticles }: Props) {
  const toc = [
    ...article.sections.map((s) => ({ id: s.id, label: s.title })),
    ...(article.table ? [{ id: 'comparison', label: 'Comparison Table' }] : []),
    { id: 'faq', label: 'Frequently Asked Questions' },
  ];

  const articleUrl = `${SITE_URL}${getArticleUrl(article.slug)}`;

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        image: article.image,
        datePublished: '2026-08-01',
        dateModified: '2026-08-20',
        inLanguage: 'en',
        mainEntityOfPage: articleUrl,
        author: {
          '@type': 'Organization',
          name: 'USFans Spreadsheet Editorial Team',
        },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: {
            '@type': 'ImageObject',
            url: SITE_LOGO_URL,
          },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: article.title, item: articleUrl },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: article.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 pt-6">
          <nav className="mx-auto max-w-4xl flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary-600">Home</Link>
            <i className="ri-arrow-right-s-line text-foreground-300" />
            <Link to="/" className="hover:text-primary-600">Guides</Link>
            <i className="ri-arrow-right-s-line text-foreground-300" />
            <span className="text-foreground-800">{article.category}</span>
          </nav>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="mx-auto max-w-4xl">
            {/* Header */}
            <header>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold whitespace-nowrap">
                <i className={`${article.icon} text-sm mr-1`} />
                {article.category}
              </span>
              <h1 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-foreground-600 text-sm md:text-base leading-relaxed">{article.description}</p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line text-sm" /> Editorial Team
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-sm" /> {article.updated}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-sm" /> {article.readTime}
                </span>
              </div>
            </header>

            {/* Hero image */}
            <div className="mt-8 w-full h-64 md:h-96 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={`${article.title} — USFans Spreadsheet`}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Quick answer */}
            <div className="mt-8 p-5 rounded-lg bg-primary-50 border border-primary-200">
              <p className="text-sm font-bold text-primary-800 flex items-center gap-2">
                <i className="ri-flashlight-line text-base" /> Quick Answer
              </p>
              <p className="mt-2 text-sm text-foreground-700 leading-relaxed">{article.quickAnswer}</p>
            </div>

            {/* Key takeaways */}
            <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200">
              <p className="text-sm font-bold text-foreground-900">Key Takeaways</p>
              <ul className="mt-3 space-y-2 text-sm text-foreground-700">
                {article.takeaways.map((takeaway) => (
                  <li key={takeaway} className="flex gap-2">
                    <i className="ri-check-line text-primary-600 mt-0.5" />
                    {takeaway}
                  </li>
                ))}
              </ul>
            </div>

            {/* Table of contents */}
            <nav className="mt-8 p-5 rounded-lg bg-background-100 border border-background-200" aria-label="Table of contents">
              <p className="text-sm font-bold text-foreground-900">Table of Contents</p>
              <ol className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {toc.map((item, i) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className="flex items-center gap-2 text-foreground-700 hover:text-primary-600">
                      <span className="text-primary-600 font-semibold">{i + 1}.</span>
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Sections */}
            {article.sections.map((section) => (
              <section key={section.id} id={section.id} className="mt-10">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">{section.title}</h2>
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="mt-4 text-foreground-700 text-sm md:text-base leading-relaxed">
                    {p}
                  </p>
                ))}
                {section.list && (
                  <ListRenderer style={section.list.style} items={section.list.items} />
                )}
              </section>
            ))}

            {/* Comparison table */}
            {article.table && (
              <section id="comparison" className="mt-10">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">Comparison Table</h2>
                <div className="mt-5 overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-sm text-left min-w-[560px]">
                    <thead>
                      <tr className="bg-background-100 text-foreground-900">
                        {article.table.headers.map((h) => (
                          <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200">
                      {article.table.rows.map((row) => (
                        <tr key={row[0]} className="hover:bg-background-100">
                          <td className="px-4 py-3 font-semibold text-foreground-900">{row[0]}</td>
                          {row.slice(1).map((cell, i) => (
                            <td key={i} className="px-4 py-3 text-foreground-700">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* FAQ */}
            <section id="faq" className="mt-10">
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">Frequently Asked Questions</h2>
              <div className="mt-5 space-y-3">
                {article.faq.map((item) => (
                  <details key={item.q} className="rounded-lg border border-background-200 bg-background-50 group">
                    <summary className="flex items-center justify-between gap-3 px-5 py-4 text-sm md:text-base font-semibold text-foreground-900 cursor-pointer list-none">
                      {item.q}
                      <i className="ri-add-line text-foreground-500 group-open:rotate-45 transition-transform" />
                    </summary>
                    <p className="px-5 pb-4 text-sm text-foreground-700 leading-relaxed">{item.a}</p>
                  </details>
                ))}
              </div>
            </section>

            {/* Disclosure */}
            <div className="mt-10 p-5 rounded-lg bg-accent-100 border border-accent-200 text-sm text-foreground-800 leading-relaxed">
              <strong>Disclosure:</strong> {article.disclosure}
            </div>

            {/* Related articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">Related Articles</h2>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.slug}
                      to={getArticleUrl(rel.slug)}
                      className="group flex items-start gap-3 p-4 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors"
                    >
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 shrink-0">
                        <i className={`${rel.icon} text-lg`} />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-700">{rel.title}</p>
                        <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary-600">
                          Read article
                          <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <div className="mt-8 p-6 md:p-8 rounded-lg bg-foreground-950 text-background-50">
              <h2 className="text-xl md:text-2xl font-heading font-semibold">Ready to Explore the Resources?</h2>
              <p className="mt-2 text-sm text-background-50/80 leading-relaxed">
                Return to the resource hub to browse categories and guides, or continue to the main website to explore products.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-home-4-line text-base" />
                  Back to Home
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-background-50 text-foreground-950 text-sm font-semibold whitespace-nowrap hover:bg-background-200 transition-colors"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line text-base" />
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </div>
  );
}

function ListRenderer({ style, items }: { style: 'ordered' | 'unordered' | 'checklist'; items: string[] }) {
  if (style === 'ordered') {
    return (
      <ol className="mt-4 space-y-3 text-sm md:text-base text-foreground-700">
        {items.map((item, i) => (
          <li key={i} className="flex gap-3">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold shrink-0">{i + 1}</span>
            {item}
          </li>
        ))}
      </ol>
    );
  }

  if (style === 'checklist') {
    return (
      <ul className="mt-4 space-y-2.5 text-sm md:text-base text-foreground-700">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <i className="ri-checkbox-circle-line text-primary-600 mt-0.5" />
            {item}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="mt-4 space-y-2.5 text-sm md:text-base text-foreground-700 list-disc pl-5">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}